import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';

import {
  setIsSubmitting,
  setAdjustmentSaved,
  setAdjustmentLoaded,
  clearFormErrors,
  setFormErrors,
  updateAdjustmentField,
  setAdjustmentForm,
  clearAdjustmentForm,
  updateSelectedAdjustmentDetailField,
} from '../../store/slices/inventory/adjustment/inventoryAdjustmentSlice';

import InventoryAdjustmentService from '../../services/inventory/adjustment/InventoryAdjustmentService';
import useLoading from '../useLoading';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';

import {
  InventoryAdjustmentCreateDTO,
  InventoryAdjustmentUpdateDTO,
} from '../../types/inventory/adjustment/InventoryAdjustmentTypes';
import { InventoryAdjustmentFormValues } from '../../types/inventory/adjustment/nventoryAdjustmentFormValues';
import useModals from '../useModals';
import { InventoryAdjustmentDetail } from '../../types/inventory/adjustment/InventoryAdjustmentDetail';
import { closeAdjustmentModal, openAdjustmentDetailModal, updateAdjustmentStatus } from '../../store/slices/inventory/inventoryAdjustmentQuerySlice';

export const useInventoryAdjustment = () => {
  const dispatch: AppDispatch = useDispatch();

  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { show } = useAlerts();
  const { openModal } = useModals();
  const { handle } = useApiErrorHandler();

  const {
    form,
    formErrors,
    isSubmitting,
    adjustmentSaved,
    adjustmentLoaded,
    selectedDetail,
  } = useSelector((state: RootState) => state.inventoryAdjustment);

  /* ───────────────────────────────
   * FORM
   * ─────────────────────────────── */
  const updateField = <K extends keyof InventoryAdjustmentFormValues>(
    field: K,
    value: InventoryAdjustmentFormValues[K]
  ) => {
    dispatch(updateAdjustmentField({ field, value }));
  };

  const updateSelectedDetailField = (
    field: keyof InventoryAdjustmentDetail,
    value: any
  ) => {
    dispatch(
      updateSelectedAdjustmentDetailField({
        field,
        value,
      })
    );
  };

  const setForm = (data: InventoryAdjustmentFormValues) => {
    dispatch(setAdjustmentForm(data));
  };

  const clearForm = () => {
    dispatch(clearAdjustmentForm());
  };

  const setSubmitting = (value: boolean) => {
    dispatch(setIsSubmitting(value));
  };

  const openEditDetailModal = () => {
    dispatch(setAdjustmentLoaded(true));
  };

  const closeEditDetailModal = () => {
    dispatch(setAdjustmentLoaded(false));
  };


  /* ───────────────────────────────
   * VALIDATION
   * ─────────────────────────────── */
  const validateHeaderForm = () => {
    const errors: Record<string, string> = {};

    if (!form.branchId) {
      errors.branchId = 'Debe seleccionar una sucursal';
    }

    if (!form.reason || !form.reason.trim()) {
      errors.reason = 'Debe ingresar el motivo del ajuste';
    }

    if (Object.keys(errors).length > 0) {
      dispatch(setFormErrors(errors));
      return false;
    }

    dispatch(clearFormErrors());
    return true;
  };

  /* ───────────────────────────────
   * CRUD HEADER
   * ─────────────────────────────── */
  const createAdjustment = async (payload: InventoryAdjustmentCreateDTO) => {
    if (!validateHeaderForm()) return;

    try {
      dispatch(setIsSubmitting(true));
      showLoadingIndicator();

      const response = await InventoryAdjustmentService.create(payload);
      console.log(response);
      const { type, title, message, data, statusCode } = response;


      if (statusCode === 200 || statusCode === 201) {
        dispatch(setAdjustmentForm(data));
        dispatch(setAdjustmentSaved(true));
        openModal(type, { title, message, });
      } else {
        show('error', 'Error', 'No se pudo crear el ajuste.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setIsSubmitting(false));
      hideLoadingIndicator();
    }
  };

  const updateHeader = async (
    id: number,
    payload: InventoryAdjustmentUpdateDTO
  ) => {
    try {
      dispatch(setIsSubmitting(true));
      showLoadingIndicator();

      const response = await InventoryAdjustmentService.update(id, payload);
      console.log(response);
      const { type, title, message, data, statusCode } = response;


      if (statusCode === 200) {
        dispatch(setAdjustmentForm(data));
        openModal(type, { title, message, });
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setIsSubmitting(false));
      hideLoadingIndicator();
    }
  };

  const getById = async (id: number) => {
    try {
      showLoadingIndicator();
      const response = await InventoryAdjustmentService.getById(id);

      console.log(response);

      if (response?.statusCode === 200) {
        dispatch(setAdjustmentForm(response.data));
        dispatch(setAdjustmentLoaded(true));
        dispatch(openAdjustmentDetailModal());
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const finalize = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await InventoryAdjustmentService.finalize(id);
      console.log(response);

      const { statusCode, type, title, message } = response;
      if (statusCode === 200) {
        openModal(type, { title, message, });
        dispatch(clearAdjustmentForm());
        dispatch(closeAdjustmentModal());
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  /* ───────────────────────────────
 * FLOW ACTIONS
 * ─────────────────────────────── */

  const approve = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await InventoryAdjustmentService.approve(id);
      const { statusCode, data, title, message } = response;

      if (statusCode === 200) {
        dispatch(updateAdjustmentStatus(data));
        show('success', title, message);
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const reject = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await InventoryAdjustmentService.reject(id);
      const { statusCode, data, title, message, type } = response;

      if (statusCode === 200) {
        dispatch(updateAdjustmentStatus(data));
        openModal(type, { title, message });
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const cancel = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await InventoryAdjustmentService.cancel(id);
      const { statusCode, data, title, message, type } = response;

      if (statusCode === 200) {
        dispatch(updateAdjustmentStatus(data));
        openModal(type, { title, message });
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const revert = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await InventoryAdjustmentService.revert(id);
      const { statusCode, data, title, message, type } = response;

      if (statusCode === 200) {
        dispatch(updateAdjustmentStatus(data));
        openModal(type, { title, message });
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  return {
    // state
    form,
    formErrors,
    isSubmitting,
    adjustmentSaved,
    adjustmentLoaded,
    selectedDetail,

    // form
    updateField,
    updateSelectedDetailField,
    setForm,
    clearForm,
    setSubmitting,

    // header
    createAdjustment,
    finalize,
    updateHeader,
    getById,

    // flow
    approve,
    reject,
    cancel,
    revert,

    validateHeaderForm,

    openEditDetailModal,
    closeEditDetailModal,
  };
};
